<template>
	<transition name="bm-fade">
		<div v-if="visible" class="bm-dialog" @click.self="close">
			<div class="bm-dialog__panel">
				<header class="bm-dialog__head">
					<h3>{{ title }}</h3>
					<button type="button" class="bm-dialog__close" @click="close">✕</button>
				</header>

				<div class="bm-dialog__body">
					<template v-if="isBookmark">
						<label class="bm-field">
							<span>标题</span>
							<input v-model="form.title" type="text" maxlength="200" placeholder="例如 GitHub">
						</label>
						<label class="bm-field">
							<span>地址</span>
							<input v-model="form.url" type="text" maxlength="1000" placeholder="https://github.com">
						</label>
						<p v-if="metaHint" class="bm-dialog__meta-hint" :class="{'bm-dialog__meta-hint--done': metaDone}">{{ metaHint }}</p>
						<label class="bm-field">
							<span>所属分类</span>
							<select v-model.number="form.categoryId">
								<option v-for="category in categories" :key="category.id" :value="category.id">{{ category.name }}</option>
							</select>
						</label>
						<div class="bm-field">
							<span>图标</span>
							<IconPicker v-model="form.icon" />
						</div>
						<label class="bm-field">
							<span>描述</span>
							<textarea v-model="form.description" maxlength="500" rows="2" placeholder="可选"></textarea>
						</label>
					</template>

					<template v-else>
						<label class="bm-field">
							<span>分类名称</span>
							<input v-model="form.name" type="text" maxlength="100" placeholder="例如 常用工具">
						</label>
						<div class="bm-field">
							<span>分类图标</span>
							<IconPicker v-model="form.icon" />
						</div>
					</template>

					<p v-if="error" class="bm-dialog__error">{{ error }}</p>
				</div>

				<footer class="bm-dialog__foot">
					<button type="button" class="bm-btn bm-btn--ghost" :disabled="saving" @click="close">取消</button>
					<button type="button" class="bm-btn" :disabled="saving" @click="submit">{{ saving ? '保存中...' : '保存' }}</button>
				</footer>
			</div>
		</div>
	</transition>
</template>

<script>
	import IconPicker from './IconPicker'
	import {fetchBookmarkMeta} from '@/api/bookmark'

	const HTTPS_PREFIX = 'https://'
	const PROTOCOL_SEPARATOR = '://'

	/**
	 * 网址输入停止后延迟多久发起识别（ms）
	 */
	const META_FETCH_DELAY = 600

	export default {
		name: 'EditDialog',
		components: {IconPicker},
		props: {
			/**
			 * 是否显示弹窗
			 */
			visible: {
				type: Boolean,
				default: false
			},
			/**
			 * 弹窗类型：bookmark / category
			 */
			type: {
				type: String,
				default: 'bookmark'
			},
			/**
			 * 编辑对象，新增时为 null
			 */
			item: {
				type: Object,
				default: null
			},
			/**
			 * 分类列表，新增书签时用于选择所属分类
			 */
			categories: {
				type: Array,
				default: () => []
			},
			/**
			 * 新增书签时的默认分类
			 */
			defaultCategoryId: {
				type: Number,
				default: null
			},
			/**
			 * 是否正在保存
			 */
			saving: {
				type: Boolean,
				default: false
			}
		},
		data() {
			return {
				form: {
					id: null,
					title: '',
					url: '',
					icon: '',
					description: '',
					categoryId: null,
					name: ''
				},
				error: '',
				metaHint: '',
				metaDone: false
			}
		},
		computed: {
			/**
			 * 是否为书签表单
			 */
			isBookmark() {
				return this.type === 'bookmark'
			},
			/**
			 * 弹窗标题
			 */
			title() {
				if (!this.isBookmark) {
					return this.item ? '编辑分类' : '新增分类'
				}
				return this.item ? '编辑书签' : '新增书签'
			}
		},
		watch: {
			/**
			 * 弹窗打开时初始化表单
			 *
			 * @param {Boolean} value 是否显示
			 */
			visible(value) {
				if (value) {
					this.initForm()
				} else {
					this.clearMetaTimer()
				}
			},
			/**
			 * 网址输入停止后自动识别标题与图标
			 *
			 * <p>编辑已有书签时仅在用户改动网址后才触发，避免打开弹窗就发起请求。</p>
			 */
			'form.url'() {
				if (!this.isBookmark) {
					return
				}
				this.clearMetaTimer()
				const rawUrl = (this.form.url || '').trim()
				if (!rawUrl || rawUrl === this._initialUrl) {
					this.metaHint = ''
					return
				}
				this.metaHint = ''
				this.metaDone = false
				this._metaTimer = window.setTimeout(() => {
					this._metaTimer = null
					this.fetchMetaForUrl(rawUrl)
				}, META_FETCH_DELAY)
			}
		},
		beforeDestroy() {
			this.clearMetaTimer()
		},
		methods: {
			/**
			 * 初始化表单数据
			 */
			initForm() {
				const item = this.item || {}
				if (this.isBookmark) {
					const fallbackCategoryId = this.categories.length ? this.categories[0].id : null
					this.form = {
						id: item.id || null,
						title: item.title || '',
						url: item.url || '',
						icon: item.icon || '',
						description: item.description || '',
						categoryId: item.categoryId || this.defaultCategoryId || fallbackCategoryId,
						name: ''
					}
				} else {
					this.form = {
						id: item.id || null,
						title: '',
						url: '',
						icon: item.icon || '',
						description: '',
						categoryId: null,
						name: item.name || ''
					}
				}
				this.error = ''
				this.metaHint = ''
				this.metaDone = false
				this._initialUrl = (item.url || '').trim()
			},
			/**
			 * 关闭弹窗
			 */
			close() {
				if (this.saving) {
					return
				}
				this.$emit('close')
			},
			/**
			 * 提交表单
			 *
			 * <p>完成基础校验与地址规范化后交给父组件保存。</p>
			 */
			submit() {
				this.error = ''
				if (!this.isBookmark) {
					this.submitCategory()
					return
				}
				const title = (this.form.title || '').trim()
				if (!title) {
					this.error = '书签标题不能为空'
					return
				}
				const rawUrl = (this.form.url || '').trim()
				if (!rawUrl) {
					this.error = '书签地址不能为空'
					return
				}
				const url = this.normalizeUrl(rawUrl)
				if (!url) {
					this.error = '书签地址仅支持 http/https'
					return
				}
				if (!this.form.categoryId) {
					this.error = '请选择所属分类'
					return
				}
				this.$emit('save', {
					id: this.form.id,
					categoryId: this.form.categoryId,
					title,
					url,
					icon: (this.form.icon || '').trim(),
					description: (this.form.description || '').trim()
				})
			},
			/**
			 * 提交分类表单
			 */
			submitCategory() {
				const name = (this.form.name || '').trim()
				if (!name) {
					this.error = '分类名称不能为空'
					return
				}
				this.$emit('save', {
					id: this.form.id,
					name,
					icon: (this.form.icon || '').trim()
				})
			},
			/**
			 * 规范化 http/https 地址
			 *
			 * @param {String} rawUrl 原始地址
			 * @returns {String} 规范化地址，非法时返回空字符串
			 */
			normalizeUrl(rawUrl) {
				if (/^http:\/\//i.test(rawUrl) || /^https:\/\//i.test(rawUrl)) {
					return rawUrl
				}
				if (rawUrl.includes(PROTOCOL_SEPARATOR)) {
					return ''
				}
				return HTTPS_PREFIX + rawUrl
			},
			/**
			 * 请求后端识别网页标题与图标
			 *
			 * <p>仅在字段为空时回填，不覆盖用户已填内容；识别失败保持静默。</p>
			 *
			 * @param {String} rawUrl 用户输入的原始地址
			 */
			fetchMetaForUrl(rawUrl) {
				const url = this.normalizeUrl(rawUrl)
				if (!url) {
					return
				}
				this.metaHint = '正在识别标题与图标...'
				this.metaDone = false
				fetchBookmarkMeta(url).then(res => {
					if ((this.form.url || '').trim() !== rawUrl) {
						// 用户已修改网址，丢弃过期响应
						return
					}
					if (res.code !== 200 || !res.data) {
						this.metaHint = ''
						return
					}
					if (!this.form.title && res.data.title) {
						this.form.title = res.data.title
					}
					if (!this.form.icon && res.data.icon) {
						this.form.icon = res.data.icon
					}
					this.metaDone = Boolean(res.data.title || res.data.icon)
					this.metaHint = this.metaDone ? '✓ 已自动识别标题与图标' : ''
				}).catch(() => {
					this.metaHint = ''
				})
			},
			/**
			 * 清除网址识别延时器
			 */
			clearMetaTimer() {
				if (this._metaTimer) {
					window.clearTimeout(this._metaTimer)
					this._metaTimer = null
				}
			}
		}
	}
</script>

<style scoped>
	.bm-dialog {
		position: fixed;
		inset: 0;
		z-index: 9999;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 20px;
		background: rgba(3, 6, 20, 0.55);
		backdrop-filter: blur(8px);
	}

	.bm-dialog__panel {
		width: min(440px, 94vw);
		max-height: 90vh;
		overflow: auto;
		padding: 22px 24px 20px;
		border-radius: 24px;
		background: linear-gradient(180deg, rgba(38, 44, 76, 0.96), rgba(26, 30, 54, 0.96));
		border: 1px solid rgba(255, 255, 255, 0.12);
		box-shadow: 0 30px 70px rgba(0, 0, 0, 0.6);
	}

	.bm-dialog__head {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 18px;
	}

	.bm-dialog__head h3 {
		margin: 0;
		color: #eef2ff;
		font-size: 18px;
		font-weight: 600;
	}

	.bm-dialog__close {
		width: 28px;
		height: 28px;
		border: none;
		border-radius: 9px;
		background: rgba(255, 255, 255, 0.1);
		color: rgba(226, 233, 255, 0.8);
		cursor: pointer;
		line-height: 1;
		transition: background 0.2s ease;
	}

	.bm-dialog__close:hover {
		background: rgba(255, 255, 255, 0.18);
	}

	.bm-dialog__body {
		display: flex;
		flex-direction: column;
		gap: 12px;
	}

	.bm-field {
		display: flex;
		flex-direction: column;
		gap: 6px;
	}

	.bm-field span {
		color: rgba(200, 212, 248, 0.62);
		font-size: 12px;
	}

	.bm-field input,
	.bm-field select,
	.bm-field textarea {
		width: 100%;
		padding: 10px 12px;
		border: 1px solid rgba(255, 255, 255, 0.12);
		border-radius: 12px;
		background: rgba(255, 255, 255, 0.06);
		color: #eaf0ff;
		font-size: 14px;
		outline: none;
		transition: border-color 0.2s ease, background 0.2s ease;
	}

	.bm-field select option {
		background: #1a1f38;
		color: #e9edff;
	}

	.bm-field input::placeholder,
	.bm-field textarea::placeholder {
		color: rgba(180, 192, 235, 0.45);
	}

	.bm-field input:focus,
	.bm-field select:focus,
	.bm-field textarea:focus {
		border-color: rgba(140, 164, 255, 0.6);
		background: rgba(255, 255, 255, 0.11);
	}

	.bm-field textarea {
		resize: vertical;
		min-height: 52px;
	}

	.bm-dialog__error {
		margin: 2px 0 0;
		color: #ff8199;
		font-size: 12px;
	}

	.bm-dialog__meta-hint {
		margin: -4px 0 0;
		color: rgba(190, 202, 245, 0.6);
		font-size: 12px;
	}

	.bm-dialog__meta-hint--done {
		color: #7ee2a8;
	}

	.bm-dialog__foot {
		display: flex;
		justify-content: flex-end;
		gap: 10px;
		margin-top: 20px;
	}

	.bm-btn {
		padding: 9px 22px;
		border: none;
		border-radius: 12px;
		background: linear-gradient(135deg, #6d7dff, #b06bff);
		color: #fff;
		cursor: pointer;
		font-size: 14px;
		box-shadow: 0 10px 24px rgba(109, 125, 255, 0.36);
		transition: opacity 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
	}

	.bm-btn:hover {
		transform: translateY(-1px);
		box-shadow: 0 12px 28px rgba(109, 125, 255, 0.46);
	}

	.bm-btn:disabled {
		opacity: 0.6;
		cursor: not-allowed;
		transform: none;
		box-shadow: none;
	}

	.bm-btn--ghost {
		background: rgba(255, 255, 255, 0.1);
		color: rgba(226, 233, 255, 0.8);
		box-shadow: none;
	}

	.bm-btn--ghost:hover {
		background: rgba(255, 255, 255, 0.17);
	}

	.bm-fade-enter-active,
	.bm-fade-leave-active {
		transition: opacity 0.2s ease;
	}

	.bm-fade-enter,
	.bm-fade-leave-to {
		opacity: 0;
	}
</style>
